import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useWeather } from '@/hooks/useWeather';
import { Cloud, CloudRain, Droplets, Sun, ThermometerSun, Wind } from 'lucide-react';
import useTranslation from '@/hooks/use-translation';
import { useMode } from '@/contexts/ModeContext';
import { cn } from '@/lib/utils';

const Weather = () => {
  const { weatherData, isLoading, error } = useWeather();
  const { translate } = useTranslation();
  const { mode } = useMode();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-primary">
          <Cloud className="h-12 w-12" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-6">
        <p className="text-destructive">{error.message}</p>
      </div>
    );
  }

  const getWeatherAdvice = () => {
    if (!weatherData) return null;

    const { temperature, humidity, rainfall } = weatherData;
    
    if (mode === 'agriculture') {
      if (rainfall > 0) {
        return translate("Natural rainfall detected. Consider adjusting irrigation schedule.");
      }
      if (temperature > 30) {
        return translate("High temperature alert. Increase irrigation frequency.");
      }
      if (humidity > 70) {
        return translate("High humidity detected. Monitor for potential plant diseases.");
      }
      return translate("Weather conditions are optimal for regular irrigation.");
    } else {
      if (rainfall > 0) {
        return translate("Rain expected. Turn off outdoor sprinklers.");
      }
      if (temperature > 30) {
        return translate("Hot weather alert. Consider evening watering for gardens.");
      }
      if (humidity > 70) {
        return translate("High humidity. Reduce water usage for outdoor activities.");
      }
      return translate("Weather conditions are suitable for normal water usage.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {translate("Weather Insights")}
        </h1>
        <p className="text-muted-foreground">
          {translate(
            mode === 'household'
              ? "Weather information to help optimize your household water usage"
              : "Weather data to improve irrigation efficiency"
          )}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {weatherData && (
          <>
            <WeatherStat
              icon={<ThermometerSun className="h-5 w-5 text-orange-500" />}
              label={translate("Temperature")}
              value={`${weatherData.temperature}°C`}
            />
            <WeatherStat
              icon={<Droplets className="h-5 w-5 text-blue-500" />}
              label={translate("Humidity")}
              value={`${weatherData.humidity}%`}
            />
            <WeatherStat
              icon={<CloudRain className="h-5 w-5 text-indigo-500" />}
              label={translate("Rainfall")}
              value={`${weatherData.rainfall} mm`}
            />
          </>
        )}
      </div>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>{translate("Weather Forecast")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {weatherData?.forecast.map((day, index) => (
              <div
                key={index}
                className={cn(
                  "flex items-center justify-between p-4 rounded-lg transition-colors",
                  "hover:bg-muted/50"
                )}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-full bg-primary/10">
                    {day.icon.includes('rain') ? (
                      <CloudRain className="h-5 w-5 text-primary" />
                    ) : (
                      <Sun className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{day.date}</p>
                    <p className="text-sm text-muted-foreground">
                      {day.description}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">{day.temperature}°C</p>
                  <p className="text-sm text-muted-foreground">
                    {day.rainfall > 0 ? `${day.rainfall}mm` : translate("No rain")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{translate("Water Management Advice")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{getWeatherAdvice()}</p>
        </CardContent>
      </Card>
    </div>
  );
};

const WeatherStat = ({ icon, label, value }: { 
  icon: React.ReactNode; 
  label: string; 
  value: string; 
}) => {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="p-3 rounded-full bg-primary/10">
          {icon}
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default Weather; 